/**
 * Calibres → Needs assessment: the lineage-placeholder work queue (memo 5.5, D8).
 *
 * Lists every calibre flagged `isLineagePlaceholder` ("revision unspecified")
 * with how many of its refs still sit under a BRAND-scoped alias — the refs a
 * curator can move to a specific revision with evidence. Refs under un-branded
 * aliases are counted apart: they stay on the placeholder by design.
 *
 * Counting reads each placeholder's linked refs, so it runs on demand per row
 * (or for the visible rows), never on open.
 */
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { getLineagePlaceholders, loadBrandAkaGroups } from '../../services/calibreLineage';
import type { CustomCalibreEntry } from '../../services/firebase';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Open the calibre's editor, where its Revisions card holds the reassignment tool. */
  onOpenCalibre: (calibreId: string) => void;
}

interface Counts {
  brandAka: number;
  brands: number;
  fallback: number;
}

export function NeedsAssessmentModal({ isOpen, onClose, onOpenCalibre }: Props) {
  const [rows, setRows] = useState<CustomCalibreEntry[] | null>(null);
  const [counts, setCounts] = useState<Record<string, Counts | 'loading' | 'error'>>({});
  const [filter, setFilter] = useState('');
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setRows(null);
    setCounts({});
    getLineagePlaceholders()
      .then(setRows)
      .catch((err) => {
        console.error('Failed to load lineage placeholders', err);
        toast.error('Could not load the Needs Assessment queue');
        setRows([]);
      });
  }, [isOpen]);

  const visible = useMemo(() => {
    const term = filter.trim().toLowerCase();
    return (rows ?? []).filter((r) => {
      if (term && !`${r.name} ${r.manufacturerId ?? ''} ${r.brandId ?? ''}`.toLowerCase().includes(term)) return false;
      if (onlyUnassigned) {
        const c = counts[r.id];
        return typeof c === 'object' && c.brandAka > 0;
      }
      return true;
    });
  }, [rows, filter, onlyUnassigned, counts]);

  const count = async (r: CustomCalibreEntry) => {
    setCounts((prev) => ({ ...prev, [r.id]: 'loading' }));
    try {
      const { groups, fallbackCount } = await loadBrandAkaGroups(r);
      const withRefs = groups.filter((g) => g.refs.length > 0);
      setCounts((prev) => ({
        ...prev,
        [r.id]: {
          brandAka: withRefs.reduce((n, g) => n + g.refs.length, 0),
          brands: new Set(withRefs.map((g) => g.brandId)).size,
          fallback: fallbackCount,
        },
      }));
    } catch (err) {
      console.error('Count failed for', r.id, err);
      setCounts((prev) => ({ ...prev, [r.id]: 'error' }));
    }
  };

  const countVisible = async () => {
    for (const r of visible) if (!counts[r.id] || counts[r.id] === 'error') await count(r);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Needs assessment — lineage placeholders" size="xl">
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Calibres marked &ldquo;revision unspecified&rdquo;. Brand-AKA refs can move to a specific revision with
          evidence — open the calibre and use its Revisions card. Refs under un-branded aliases stay on the
          placeholder.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by name, manufacturer or brand"
            className="flex-1 min-w-[12rem] rounded-md border border-gray-300 px-3 py-1.5 text-sm"
          />
          <label className="flex items-center gap-1.5 text-sm text-gray-700">
            <input type="checkbox" checked={onlyUnassigned} onChange={(e) => setOnlyUnassigned(e.target.checked)} />
            Only with unassigned brand-AKA refs
          </label>
          <Button size="sm" variant="secondary" onClick={() => void countVisible()} disabled={!rows || visible.length === 0}>
            Count refs for shown rows
          </Button>
        </div>
        {onlyUnassigned && (
          <p className="text-xs text-gray-500">Rows appear here once their refs have been counted.</p>
        )}
        {rows === null ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500">No calibre is marked as a lineage placeholder.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Calibre</th>
                <th className="px-3 py-2 text-left font-medium">Manufacturer</th>
                <th className="px-3 py-2 text-left font-medium">Brand-AKA refs</th>
                <th className="px-3 py-2 text-left font-medium">Stay (un-branded)</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.map((r) => {
                const c = counts[r.id];
                return (
                  <tr key={r.id}>
                    <td className="px-3 py-2 text-gray-900">{r.name}</td>
                    <td className="px-3 py-2 text-gray-500">{r.manufacturerId ?? '—'}</td>
                    <td className="px-3 py-2">
                      {c === undefined ? (
                        <Button size="sm" variant="ghost" onClick={() => void count(r)}>
                          Count
                        </Button>
                      ) : c === 'loading' ? (
                        <span className="text-gray-400">Counting…</span>
                      ) : c === 'error' ? (
                        <span className="text-red-600">Failed</span>
                      ) : (
                        <span className={c.brandAka > 0 ? 'text-amber-700 font-medium' : 'text-gray-500'}>
                          {c.brandAka} across {c.brands} brand(s)
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{typeof c === 'object' ? c.fallback : ''}</td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => onOpenCalibre(r.id)}>
                        Open
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}
